/**
 * Claim charges (« Charges » tab — fiche 22742): one row per guarantee with
 * provision (PROV_RESTANTE), règlement, recours estimé / encaissé and
 * franchise in euros, then a bold « Total » row (desktop and mobile — see
 * `./claimChargesConfig.ts` for why it is an appended row rather than the
 * desktop-only DataList footer). Overrides / publish / loading / error /
 * empty states are handled by the shared `DataTableCard`.
 *
 * NOT registered by itself — registered as `extranet.claimChargesCard`.
 */

import { useMemo } from 'react';
import { Wallet, type LucideIcon } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import type { ClaimChargeRow } from '../../api/claimsService';
import { DataTableCard } from '../shared/DataTableCard';
import { buildClaimChargesConfig } from './claimChargesConfig';
import './claimChargesRenderers';

export interface ClaimChargesCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row (the tab names the content). */
  title?: string;
  icon?: LucideIcon;
  /** The guarantees, in API order (the Total row is computed by the card). */
  rows: readonly ClaimChargeRow[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function ClaimChargesCard({
  componentId,
  title,
  icon = Wallet,
  rows,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<ClaimChargesCardProps>) {
  const config = useMemo(() => buildClaimChargesConfig(componentId, rows), [componentId, rows]);

  return (
    <DataTableCard
      componentId={componentId}
      title={title}
      icon={icon}
      config={config}
      rowCount={rows.length}
      loading={loading}
      error={error}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
